import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import NovelCard from "@/components/feature/NovelCard";
import {
  WebPageSchema,
  BreadcrumbListSchema,
} from "@/components/base/StructuredData";
import { categoryList, categoryNovels } from "@/mocks/novel";

const sortOptions = [
  { value: "rating", label: "评分最高" },
  { value: "newest", label: "最新发布" },
  { value: "popular", label: "最受欢迎" },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "wuxia");

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeCategory]);

  const currentCategory = useMemo(
    () => categoryList.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const novels = useMemo(
    () => categoryNovels[activeCategory] || [],
    [activeCategory]
  );

  return (
    <>
      <ScrollToTop />
      <WebPageSchema
        title={`${currentCategory?.name || ""}小说 - 二枪入一洞在线阅读 | 书香阁`}
        description={`书香阁${currentCategory?.name || ""}小说分类，收录${currentCategory?.count.toLocaleString() || 0}部精品${currentCategory?.name || ""}小说，包含二枪入一洞等热门作品。`}
        url={`https://shuxiangge.com/category/${activeCategory}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: "首页", url: "https://shuxiangge.com/" },
          { name: currentCategory?.name || "分类", url: `https://shuxiangge.com/category/${activeCategory}` },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: currentCategory?.name || "分类", to: undefined },
                ]}
              />

              <div className="mb-10">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory?.name || "分类"}
                </h1>
                <p className="text-foreground-500 text-sm mt-2">
                  共 {currentCategory?.count.toLocaleString() || 0} 部作品
                </p>
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 border-b border-background-200/70 flex-wrap">
                {categoryList.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      activeCategory === cat.slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                    <span className="text-xs opacity-70">{cat.count.toLocaleString()}</span>
                  </Link>
                ))}
              </div>

              {/* Sort */}
              <div className="flex items-center justify-between mb-8">
                <p className="text-sm text-foreground-500">
                  显示 <strong className="text-foreground-800">{novels.length}</strong> 部作品
                </p>
                <div className="flex items-center gap-2">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      className="px-3 py-1.5 text-xs rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Novel Grid */}
              {novels.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 mb-16" data-product-shop="">
                  {novels.map((novel) => (
                    <NovelCard key={novel.id} {...novel} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
                    <i className="ri-book-2-line text-3xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无作品，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}